/* Offline action queue — Failsafe System.
   Requests flagged with `{ offline: "label" }` in their axios config are stamped with an
   idempotency op-id. If the network is down they are persisted locally (with the real press
   time) and replayed in order once signal returns; the backend applies each op exactly once. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUEUE_KEY = "ab_offline_queue_v1";
const FAILED_KEY = "ab_offline_failed_v1";
export const OFFLINE_EVENT = "ab-offline-change";
export const SYNCED_EVENT = "ab-offline-synced";

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const write = (k, v) => { localStorage.setItem(k, JSON.stringify(v)); window.dispatchEvent(new CustomEvent(OFFLINE_EVENT)); };

export const offlineState = { online: typeof navigator === "undefined" ? true : navigator.onLine, cached: false, syncing: false, lastSync: null, stuck: false, failures: 0, lastError: null };
const setState = (patch) => { Object.assign(offlineState, patch); window.dispatchEvent(new CustomEvent(OFFLINE_EVENT)); };

export const getQueue = () => read(QUEUE_KEY);
export const getFailed = () => read(FAILED_KEY);
export const clearFailed = () => write(FAILED_KEY, []);
export const pendingCount = () => read(QUEUE_KEY).length;

const isNetworkError = (err) => !err.response && (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.message === "Network Error" || !navigator.onLine);
const hdrGet = (h, k) => (h && typeof h.get === "function" ? h.get(k) : h?.[k]);
const hdrSet = (h, k, v) => { if (h && typeof h.set === "function") h.set(k, v); else if (h) h[k] = v; };

api.interceptors.request.use((config) => {
  if (config.offline && !hdrGet(config.headers, "X-Offline-Op-Id")) hdrSet(config.headers, "X-Offline-Op-Id", uuid());
  return config;
});

api.interceptors.response.use(
  (res) => {
    const served = hdrGet(res.headers, "x-armorbatch-offline") === "1";
    if (served && !offlineState.cached) setState({ cached: true });
    else if (!served && offlineState.cached && res.config?.method === "get") setState({ cached: false });
    return res;
  },
  (err) => {
    const cfg = err.config || {};
    if (cfg.offline && !cfg.__replay && isNetworkError(err)) {
      const item = {
        id: hdrGet(cfg.headers, "X-Offline-Op-Id") || uuid(),
        method: cfg.method,
        url: cfg.url,
        data: typeof cfg.data === "string" ? cfg.data : JSON.stringify(cfg.data ?? null),
        label: typeof cfg.offline === "string" ? cfg.offline : `${(cfg.method || "").toUpperCase()} ${cfg.url}`,
        recorded_at: new Date().toISOString(),
      };
      write(QUEUE_KEY, [...read(QUEUE_KEY), item]);
      if (offlineState.online) setState({ online: false });
      toast.warning(`Offline · "${item.label}" saved on this device — syncs when signal returns`, { duration: 5000 });
      return Promise.resolve({ status: 202, statusText: "Queued offline", data: { queued: true, offline: true, op_id: item.id }, headers: {}, config: cfg, queued: true });
    }
    return Promise.reject(err);
  },
);

let draining = false;
export async function drainQueue() {
  if (draining || !navigator.onLine || !read(QUEUE_KEY).length) return;
  draining = true;
  setState({ syncing: true, online: true });
  let sent = 0;
  try {
    for (;;) {
      const cur = read(QUEUE_KEY);
      if (!cur.length) break;
      const item = cur[0];
      try {
        await api.request({
          method: item.method, url: item.url,
          data: item.data ? JSON.parse(item.data) : undefined,
          headers: { "X-Offline-Op-Id": item.id, "X-Offline-Recorded-At": item.recorded_at },
          __replay: true, timeout: 25000,
        });
        write(QUEUE_KEY, read(QUEUE_KEY).filter((x) => x.id !== item.id));
        sent += 1;
        if (offlineState.failures || offlineState.stuck) setState({ failures: 0, stuck: false, lastError: null });
      } catch (err) {
        if (!err.response || err.response.status === 401) {
          // Still offline, server unreachable (e.g. wrong API host / CORS) or session gone — retry later, but
          // after repeated failures WHILE the device says it is online, flag the queue as stuck so drivers
          // see it instead of silently believing their taps went through.
          const failures = (offlineState.failures || 0) + 1;
          const stuck = navigator.onLine && failures >= 3;
          setState({ failures, stuck, lastError: err.response ? `HTTP ${err.response.status} — sign in again` : (err.message || "Server unreachable") });
          break;
        }
        setState({ failures: 0 });
        const detail = err.response.data?.detail;
        write(FAILED_KEY, [{ ...item, error: typeof detail === "string" ? detail : JSON.stringify(detail || `HTTP ${err.response.status}`), failed_at: new Date().toISOString() }, ...read(FAILED_KEY)].slice(0, 20));
        write(QUEUE_KEY, read(QUEUE_KEY).filter((x) => x.id !== item.id));
      }
    }
  } finally {
    draining = false;
    setState({ syncing: false, lastSync: sent ? new Date().toISOString() : offlineState.lastSync });
    if (sent) {
      toast.success(`Synced ${sent} offline action${sent > 1 ? "s" : ""} to dispatch`);
      window.dispatchEvent(new CustomEvent(SYNCED_EVENT, { detail: { sent } }));
    }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { setState({ online: true }); setTimeout(drainQueue, 800); });
  window.addEventListener("offline", () => setState({ online: false }));
  setInterval(() => { if (read(QUEUE_KEY).length) drainQueue(); }, 20000);
  setTimeout(drainQueue, 2500);
}

export function useOffline() {
  const [, force] = useState(0);
  useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener(OFFLINE_EVENT, h);
    return () => window.removeEventListener(OFFLINE_EVENT, h);
  }, []);
  return { ...offlineState, pending: getQueue(), failed: getFailed(), drain: drainQueue, clearFailed };
}
